import {
  FeatureFlags,
  SchoolRelation,
  SyncUser,
  SyncUserSchoolRelation,
  UserRolePermission,
  UserRolePermissions,
  useSyncUserQuery,
  WithAvatar,
} from '@schooly/api';
import { SchoolUserRole } from '@schooly/constants';
import { getEnv } from '@schooly/utils/runtime-env';
import { Auth } from 'aws-amplify';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { createContext, useContext } from 'react';

export const E2E_BYPASS_AUTH = getEnv('E2E_BYPASS_AUTH') === 'true';

type UserWithPermissions = SyncUser & { permissions: UserRolePermissions[] };

type ChangePasswordParams = {
  oldPassword: string;
  newPassword: string;
};

const UNAUTHORIZED_MESSAGE = 'Unauthorized';

export const WithUser: FC<PropsWithChildren> = ({ children }) => {
  const [isAuthenticated, setAuthenticated] = useState<boolean>();

  const { data, isFetching } = useSyncUserQuery({
    enabled: !!isAuthenticated,
    retry: (failureCount, error) => {
      if (error.message === UNAUTHORIZED_MESSAGE) return false;
      if (failureCount > 3) return false;

      return true;
    },
    refetchOnMount: 'always',
  });

  const { logout, changePassword } = useMemo(() => {
    return {
      logout: async () => {
        await Auth.signOut();
        setAuthenticated(false);
      },
      changePassword: async ({ oldPassword, newPassword }: ChangePasswordParams) => {
        const user = await Auth.currentAuthenticatedUser();
        return Auth.changePassword(user, oldPassword, newPassword);
      },
    };
  }, []);

  const userWithPermissions = useMemo(() => {
    if (isAuthenticated === false) return null;
    if (isAuthenticated === undefined || !data) return undefined;

    return { ...data.user, permissions: data.permissions };
  }, [data, isAuthenticated]);

  const authenticate = useCallback(async () => {
    if (E2E_BYPASS_AUTH) {
      setAuthenticated(true);
      return;
    }
    try {
      await Auth.currentSession();
      setAuthenticated(true);
    } catch {
      setAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    authenticate();
  }, [authenticate]);

  return (
    <UserContext.Provider value={{ user: userWithPermissions, logout, changePassword, isFetching }}>
      {children}
    </UserContext.Provider>
  );
};

export interface UserContextParams {
  user?: UserWithPermissions | null;
  logout: () => Promise<void>;
  changePassword: (v: ChangePasswordParams) => Promise<any>;
  isFetching: boolean;
}

export const UserContext = createContext<UserContextParams>({
  logout: async () => {},
  changePassword: async () => {},
  isFetching: false,
});
export const useUserContext = () => useContext(UserContext);
export interface AuthContextParams {
  schoolId?: string;
  relationId?: string;
  token?: string;
  permissions: UserRolePermission[];
  currentUser?: SyncUser;
  currentStaff?: SchoolRelation & WithAvatar & { feature_flags?: FeatureFlags };
  username?: string;
  currentSchool?: SyncUserSchoolRelation['school'];
}

const defaultState: AuthContextParams = {
  permissions: [],
};
export const AuthContext = createContext<AuthContextParams>(defaultState);
export const useAuth = () => useContext(AuthContext);

export const WithAuth: FC<PropsWithChildren> = ({ children }) => {
  const { user: syncUser } = useUserContext();

  const params: AuthContextParams = useMemo(() => {
    const state: AuthContextParams = { ...defaultState };

    if (!syncUser) return state;

    const { account_email, last_selected_school_id, school_relations, permissions } = syncUser;

    if (school_relations.length) {
      const lastSelectedSchool = last_selected_school_id
        ? school_relations.find(
            (s) => s.relation_id === last_selected_school_id && s.role === SchoolUserRole.Staff,
          )
        : school_relations.find((s) => s.role === SchoolUserRole.Staff);

      const schoolRelation =
        lastSelectedSchool ??
        school_relations.find(
          (s) => s.school.id === last_selected_school_id && s.role === SchoolUserRole.Staff,
        );

      state.schoolId = schoolRelation?.school.id;
      state.relationId = schoolRelation?.relation_id;

      if (schoolRelation) {
        const {
          relation_id,
          title,
          given_name,
          last_name,
          known_as,
          profile_picture,
          feature_flags,
        } = schoolRelation;

        state.currentStaff = {
          relation_id,
          title,
          given_name,
          last_name,
          known_as,
          profile_picture,
          feature_flags,
        };

        state.currentSchool = schoolRelation.school;
      }
    }

    state.permissions = permissions;
    state.currentUser = syncUser;
    state.username = account_email;

    return state;
  }, [syncUser]);

  return <AuthContext.Provider value={params}>{children}</AuthContext.Provider>;
};
